import { inject, Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { BASE_URL_TOKEN } from '@raptor-fe/core';
import { TokenService } from '../token-service/token.service';

// Service to manage SignalR connections
@Injectable({ providedIn: 'root' })
export class SignalRService {
    private baseUrl = inject(BASE_URL_TOKEN);
    private tokenService = inject(TokenService);
    private connection: signalR.HubConnection | null = null;
    private startPromise: Promise<string | null> | null = null;

    // Start SignalR connection
    async startConnection(): Promise<string | null> {
        // Reuse an in-flight start (also dedupes concurrent callers) instead of building a second connection
        // that would orphan the first and double-register handlers.
        if (this.startPromise) {
            return this.startPromise;
        }

        // An existing connection that is Connected, Connecting or Reconnecting must not be rebuilt; automatic
        // reconnect will recover it on its own. Only rebuild when there is no connection or it is Disconnected.
        if (this.connection && this.connection.state !== signalR.HubConnectionState.Disconnected) {
            return this.connection.connectionId;
        }

        this.connection = new signalR.HubConnectionBuilder()
            .withUrl(this.baseUrl + SignalRHubs.RAPTOR_HUB, {
                withCredentials: false,
                accessTokenFactory: () => this.tokenService.getToken() || '',
            })
            .withAutomaticReconnect()
            .build();

        this.startPromise = this.connection
            .start()
            .then(() => this.connection?.connectionId ?? null)
            .catch((err) => {
                // Reset so a later call can rebuild, and never return a stale/failed connection id.
                console.error('Error starting SignalR connection:', err);
                this.connection = null;
                return null;
            })
            .finally(() => {
                this.startPromise = null;
            });

        return this.startPromise;
    }

    // Stop SignalR connection. HubConnection.stop() is valid in any state -- including Connecting/Reconnecting,
    // where it aborts the pending start -- so this must not gate on Connected: leaving a Connecting/Reconnecting
    // connection running means a later startConnection() sees a non-Disconnected state and reuses the stale
    // connection (and its handlers) instead of rebuilding for the new caller/session.
    async stopConnection(): Promise<void> {
        if (this.connection) {
            await this.connection.stop().catch((err) => console.error('Error stopping SignalR connection:', err));
            this.connection = null;
            this.startPromise = null;
        }
    }

    // Listen for notifications
    onNotification(methodName: string, callback: (data: unknown) => Promise<void>): void {
        if (this.connection) {
            this.connection.off(methodName); // Remove existing handler for the method to avoid multiple handlers
            this.connection.on(methodName, callback);
        }
    }

    stopListening(methodName: string): void {
        if (this.connection) {
            this.connection.off(methodName);
        }
    }
}

export enum SignalRHubs {
    /**
     * Notification Hub
     */
    RAPTOR_HUB = 'RaptorHub',
}

export enum SignalRMethods {
    /**
     * Notification method
     */
    NOTIFICATION = 'Notification',

    /**
     * Quote updated method, used to notify quote details page to refresh data when quote is updated in other pages.
     */
    QUOTE_UPDATED = 'QuoteUpdated',

    /**
     * Quote updated error method, used to notify quote details page to show error message when quote is updated in other pages but failed to save due to concurrency issue.
     */
    QUOTE_UPDATED_ERROR = 'QuoteUpdatedError',

    /**
     * Customer message delivery-status change, so an open conversation advances the status in place.
     */
    CUSTOMER_TEXT_STATUS_UPDATED = 'CustomerTextStatusUpdated',

    /**
     * A customer conversation gained a message not sent from the viewer's own composer (for example a
     * server-sent inspection report), so an open conversation can pull it in without a manual refresh.
     */
    CUSTOMER_TEXT_CONVERSATION_UPDATED = 'CustomerTextConversationUpdated',
}
